/**
 * The CaseApi over the Patchbay HTTP API (packages/contracts/openapi.json). Paths and bodies match
 * the contract exactly; W2 swaps the fetch calls for the generated SDK in src/lib/api/generated
 * without changing a component. Owner: web-case-composer (slice 16).
 *
 * - GETs only read (hard rule 3). Every non-GET carries `X-CSRF-Token` from MeOut.
 * - Every call has a timeout. `/send` is never retried here: a retry is the user's click and reuses
 *   the same idempotency key, so the server answers it once.
 * - A failed lookup throws UnreachableError, which the rail renders as "unreachable", never "none".
 */
import { plainMarkup } from '@patchbay/ui'
import type {
  AudienceSource,
  CaseSuggestionsOut,
  CaseUpdateOut,
  EventOut,
  FinishCaseOut,
  NextMoveOut,
  ResolutionCheckOut,
  ResolutionDraftOut,
  SuggestionOutcomesOut,
  UpdateFormOut,
} from '../../../lib/api/generated'
import { contactLabel } from '../../../lib/contact'
import { formatWhen } from '../../../lib/display-time'
import { mapsUrl } from '../../../lib/maps'
import { poText } from '../../../lib/po'
import { formatOfficeWindow } from '../../../lib/toronto-time'
import type { QueueLaneId, SystemTemplate } from '../../../lib/types'
import type { OwnersDto } from '../../case-owner/api'
import { applyExternalPanes, omitLeftoverPanes } from '../../conversations/pane-source'
import { orderPanePeople } from '../pane-people-model'
import type { CasePatchInput, TicketEditView, TicketPatchInput, TicketPatchResult, TicketReferenceSearch } from './edit-types'
import type { CaseApi, CloseConversationOut, PaneActionOut } from './port'
import { rejectedRecipientAddresses, replyAddress, replyAllForTurn, replyAllRecipients } from './reply-all'
import {
  type AddTicketContactOut,
  ApiError,
  type CandidateView,
  type CaseView,
  type ComposerImageOut,
  type ContextView,
  type CoverageView,
  type CrmAccountOut,
  type DeskAutoCountIn,
  type DeskAutoReviewOut,
  type DeskMergePreview,
  type DeskMergeResult,
  type DeskTimeMirrorState,
  type DiscardSuggestionOut,
  type DraftCardOut,
  type EditInternalNoteOut,
  FeatureOffError,
  type FirmOption,
  type Freshness,
  type MeOut,
  type NativeChatDraftDiscardOut,
  type ObligationView,
  type PatchbayDraftDiscardOut,
  type PatchbayDraftPushOut,
  type PatchbayDraftSaveIn,
  type PatchbayDraftSaveOut,
  type PendingNoteOut,
  type PresenceView,
  type RelatedView,
  type RemoveCaseOutput,
  type RestoreDeskOutput,
  type RetractInternalNoteOut,
  type SearchHit,
  type SnippetTemplate,
  type StaffNoteOut,
  type ThreadView,
  type TicketContactOptions,
  type TimeDefaultsOut,
  type TimeEntryOut,
  type TimeReviewView,
  type TimeRowView,
  type UnclassifiedTurnView,
  type UndoJoinOut,
  UnreachableError,
  type VendorOption,
  type WorkItemView,
  type WorkOrderView,
} from './types'

const GET_TIMEOUT_MS = 10_000
const SEND_TIMEOUT_MS = 45_000
const POST_TIMEOUT_MS = 15_000
const RILEY_DRAFT_TIMEOUT_MS = 170_000
const RILEY_SUMMARY_TIMEOUT_MS = 190_000

const idem = (): string =>
  typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`

type Json = Record<string, unknown>

export interface HttpOptions {
  base?: string
  fetchImpl?: typeof fetch
}

/** The event kinds the case Activity list shows (#114); the API returns the newest of these first. */
export const ACTIVITY_KINDS = [
  'auto_reply.status_restored',
  'closed_reply.courtesy',
  'closed_reply.routed',
  'closed_reply.opened',
  'closed_reply.appended',
  'closed_reply.unconfirmed',
  'closed_reply.failed',
  'case.desk_status_changed',
  'case.closed',
  'case.closed_inbound',
  'case.reopened',
  'conversation.closed',
  'next_move.set',
  'next_move.followed_up',
  'next_move.cleared',
  'next_move.dismissed',
  'next_move.done',
  'next_move.overdue',
  'case_update.part',
  'case.updated',
  'case.customer_set',
  'case.customer_adopted',
  'ticket.updated',
  'send.sent',
  'send.refused',
  'send.unchecked',
  'send.desk_status',
  'customer_followup.sent',
  'customer_followup.skipped',
  'customer_followup.failed',
  'closeout.reminder.case_sent',
  'closeout.reminder.case_drafted',
  'sla.breached',
  'work_order.created',
  'work_order.sent',
  'work_order.submitted',
  'work_order.approved',
  'work_order.returned',
  'work_order.revoked',
  'work_order.status_changed',
  'obligation.created',
  'obligation.cleared',
  'obligation.dismissed',
  'pane.merged',
  'pane.split',
  'pane.undo',
  'portal.link_issued',
  'portal.link_revoked',
  'portal.link_taken_over',
  'portal.case_settings_changed',
] as const

export function createHttpCaseApi({ base = '', fetchImpl }: HttpOptions = {}): CaseApi {
  const doFetch = (...args: Parameters<typeof fetch>) => (fetchImpl ?? globalThis.fetch)(...args)
  const inFlightGets = new Map<string, Promise<unknown>>()
  let csrf: string | null = null

  async function performCall<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    opts: { timeout?: number; signal?: AbortSignal } = {},
  ) {
    const ctl = new AbortController()
    const timer = setTimeout(() => ctl.abort(new DOMException('timeout', 'TimeoutError')), opts.timeout ?? GET_TIMEOUT_MS)
    opts.signal?.addEventListener('abort', () => ctl.abort(opts.signal?.reason), { once: true })
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (method !== 'GET') {
      headers['Content-Type'] = 'application/json'
      if (!csrf) csrf = (await me()).csrf_token
      headers['X-CSRF-Token'] = csrf
    }
    try {
      const res = await doFetch(`${base}${path}`, {
        method,
        headers,
        credentials: 'same-origin',
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: ctl.signal,
      })
      if (res.status === 204) return undefined as T
      const data = (await res.json().catch(() => ({}))) as Json
      if (!res.ok) {
        throw new ApiError(res.status, String(data.code ?? `http_${res.status}`), String(data.detail ?? res.statusText), data)
      }
      return data as T
    } finally {
      clearTimeout(timer)
    }
  }

  async function call<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    opts: { timeout?: number; signal?: AbortSignal } = {},
  ): Promise<T> {
    if (method !== 'GET' || opts.signal) return performCall<T>(method, path, body, opts)
    const key = `${path}\n${opts.timeout ?? GET_TIMEOUT_MS}`
    const existing = inFlightGets.get(key)
    if (existing) return existing as Promise<T>
    const pending = performCall<T>(method, path, body, opts).finally(() => inFlightGets.delete(key))
    inFlightGets.set(key, pending)
    return pending
  }

  async function callForm<T>(path: string, form: FormData, opts: { timeout?: number; signal?: AbortSignal } = {}) {
    const ctl = new AbortController()
    const timer = setTimeout(() => ctl.abort(new DOMException('timeout', 'TimeoutError')), opts.timeout ?? POST_TIMEOUT_MS)
    opts.signal?.addEventListener('abort', () => ctl.abort(opts.signal?.reason), { once: true })
    if (!csrf) csrf = (await me()).csrf_token
    try {
      const res = await doFetch(`${base}${path}`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrf },
        credentials: 'same-origin',
        body: form,
        signal: ctl.signal,
      })
      if (res.status === 204) return undefined as T
      const data = (await res.json().catch(() => ({}))) as Json
      if (!res.ok) throw new ApiError(res.status, String(data.code ?? `http_${res.status}`), String(data.detail ?? res.statusText), data)
      return data as T
    } finally {
      clearTimeout(timer)
    }
  }

  /** Rail reads: any failure is "unreachable" for that source. */
  async function railGet<T>(source: string, path: string): Promise<T> {
    try {
      return await call<T>('GET', path)
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.code === 'feature_off') throw new FeatureOffError(source)
      throw new UnreachableError(source, e instanceof Error ? e.message : undefined)
    }
  }

  async function timeReview(caseUuid: string): Promise<TimeReviewView> {
    const ctl = new AbortController()
    const timer = setTimeout(() => ctl.abort(new DOMException('timeout', 'TimeoutError')), GET_TIMEOUT_MS)
    try {
      const res = await doFetch(`${base}/api/v1/cases/${encodeURIComponent(caseUuid)}/time`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: ctl.signal,
      })
      const data = (await res.json().catch(() => ({}))) as Json
      if (!res.ok) {
        throw new ApiError(res.status, String(data.code ?? `http_${res.status}`), String(data.detail ?? res.statusText), data)
      }
      if (!Array.isArray(data)) throw new ApiError(502, 'time_shape_invalid', 'Patchbay returned an invalid time list')
      const header = res.headers.get('X-Desk-Time')
      const mirrorState: DeskTimeMirrorState =
        header === 'ok' || header === 'not_connected' || header === 'unreachable' || header === 'cached' ? header : 'cached'
      return { rows: mapTimeRows(data as unknown as TimeEntryOut[]), mirrorState }
    } catch (e) {
      if (e instanceof ApiError) throw new UnreachableError('Patchbay', e.message)
      throw new UnreachableError('Patchbay', e instanceof Error ? e.message : undefined)
    } finally {
      clearTimeout(timer)
    }
  }

  async function me(): Promise<MeOut> {
    const m = await call<MeOut>('GET', '/api/v1/me')
    csrf = m.csrf_token
    return m
  }

  return {
    mode: 'http',
    me,
    timeOwners: () => call<OwnersDto>('GET', '/api/v1/owners'),
    async getCase(number) {
      // Mirror first (round 6): two Patchbay reads and no Desk call. `refresh` decides whether Desk moved.
      // A queue/home link may carry the case UUID; a typed or pasted one carries C-##### or a Desk #number.
      const c = UUID_RE.test(number)
        ? await call<ApiCase>('GET', `/api/v1/cases/${encodeURIComponent(number)}`)
        : await call<ApiCase>('GET', `/api/v1/cases/by-number/${encodeURIComponent(number)}`)
      const mirror = await call<ApiCaseMirror>('GET', `/api/v1/cases/${c.id}/mirror`)
      return toCaseView(c, mirror.threads, { source: 'mirror', asOf: mirror.as_of })
    },
    async buildSummary(caseUuid) {
      const raw = await call<NonNullable<ApiCase['case_summary']>>(
        'POST',
        `/api/v1/cases/${encodeURIComponent(caseUuid)}/summary`,
        {},
        { timeout: RILEY_SUMMARY_TIMEOUT_MS },
      )
      const summary = structuredSummary(raw)
      if (!summary) throw new ApiError(502, 'summary_invalid', 'Riley returned an empty case summary.')
      return summary
    },
    async suggestTitle(caseUuid) {
      const raw = await call<{ title: string; source: 'case_summary' | 'summary_only'; summary_as_of: string }>(
        'POST',
        `/api/v1/cases/${encodeURIComponent(caseUuid)}/title-suggestion`,
        {},
        { timeout: RILEY_SUMMARY_TIMEOUT_MS },
      )
      return { title: raw.title, source: raw.source, summaryAsOf: raw.summary_as_of }
    },
    async refresh(view): Promise<Freshness> {
      const f = await call<ApiFreshness>('GET', `/api/v1/cases/${view.uuid}/freshness`)
      if (!f.checked) return { state: 'unknown', reason: f.reason }
      if (!f.stale) return { state: 'fresh' }
      // Desk moved after the mirror: only now run the full live fetch (one Desk list call per ticket per thread).
      const c = await call<ApiCase>('GET', `/api/v1/cases/${view.uuid}`)
      const threads = await call<ApiThread[]>('GET', `/api/v1/cases/${view.uuid}/threads`)
      const turns = await Promise.all(threads.map((t) => call<ApiThreadTurns>('GET', `/api/v1/threads/${t.id}/turns`)))
      const failed = turns.find((t) => t.as_of)
      if (failed) return { state: 'unknown', reason: 'live_fetch_failed' }
      return { state: 'refreshed', view: toCaseView(c, turns, { source: 'live' }) }
    },
    postNote: (caseUuid, body) => call<StaffNoteOut>('POST', `/api/v1/cases/${caseUuid}/notes`, body, { timeout: SEND_TIMEOUT_MS }),
    retractInternalNote: (turnId, idempotencyKey) =>
      call<RetractInternalNoteOut>(
        'POST',
        `/api/v1/turns/${encodeURIComponent(turnId)}/internal-note/retract`,
        { confirmed: true, idempotency_key: idempotencyKey },
        { timeout: SEND_TIMEOUT_MS },
      ),
    editInternalNote: (turnId, bodyText, idempotencyKey) =>
      call<EditInternalNoteOut>(
        'POST',
        `/api/v1/turns/${encodeURIComponent(turnId)}/internal-note/edit`,
        { body_text: bodyText, idempotency_key: idempotencyKey },
        { timeout: SEND_TIMEOUT_MS },
      ),
    uploadComposerImage: (caseUuid, file, threadUuid) => {
      const form = new FormData()
      form.append('file', file)
      if (threadUuid) form.append('thread_id', threadUuid)
      return callForm<ComposerImageOut>(`/api/v1/cases/${caseUuid}/composer-images`, form, { timeout: SEND_TIMEOUT_MS })
    },
    async reclassify(turnId, audience, partyRef) {
      await call('POST', `/api/v1/turns/${turnId}/reclassify`, { audience, party_ref: partyRef ?? null }, { timeout: POST_TIMEOUT_MS })
    },
    async noReplyNeeded(caseUuid, turnId, on) {
      await call(
        'POST',
        `/api/v1/cases/${caseUuid}/reply-classification?turn_id=${encodeURIComponent(turnId)}`,
        { classification: on ? 'no_reply_needed' : 'needs_reply' },
        {
          timeout: POST_TIMEOUT_MS,
        },
      )
    },
    discardSuggestion: (turnId) =>
      call<DiscardSuggestionOut>('POST', '/api/v1/drafts/suggestions/discard', { turn_id: turnId }, { timeout: POST_TIMEOUT_MS }),
    proposeDeleteDraft: (caseUuid, body) =>
      call<DraftCardOut>('POST', `/api/v1/cases/${caseUuid}/drafts/delete`, body, { timeout: POST_TIMEOUT_MS }),
    confirmCard: (cardId) =>
      call<DraftCardOut>('POST', `/api/v1/cards/${cardId}/confirm`, { acknowledged_warnings: [] }, { timeout: SEND_TIMEOUT_MS }),
    dismissCard: (cardId, reason) =>
      call<DraftCardOut>('POST', `/api/v1/cards/${cardId}/dismiss`, { reason: reason ?? null }, { timeout: POST_TIMEOUT_MS }),
    async credentialsCount(caseUuid) {
      const out = await railGet<{ count: number }>('Conduit', `/api/v1/cases/${caseUuid}/credentials/count`)
      return out.count
    },
    async revealCredentials(caseUuid) {
      const out = await call<{ url: string }>('POST', `/api/v1/cases/${caseUuid}/credentials/reveal`, {}, { timeout: POST_TIMEOUT_MS })
      return { url: out.url }
    },
    sendState: (id) => call('GET', `/api/v1/threads/${id}/send-state`),
    confirmCustomerConversation: (id) =>
      call('POST', `/api/v1/threads/${id}/confirm-customer-conversation`, {}, { timeout: POST_TIMEOUT_MS }),
    emailCustomer: (caseUuid, body) => call('POST', `/api/v1/cases/${caseUuid}/email-customer`, body, { timeout: SEND_TIMEOUT_MS }),
    recipients: (id) => call('GET', `/api/v1/threads/${id}/recipients`),
    searchRecipients: (q, { threadUuid, scope }) => {
      const params = new URLSearchParams({ q })
      if (threadUuid) params.set('thread_id', threadUuid)
      if (scope) params.set('scope', scope)
      return call('GET', `/api/v1/directory/recipients?${params}`)
    },
    snippets: (audience) =>
      call<SnippetTemplate[]>('GET', `/api/v1/snippets${audience ? `?audience=${encodeURIComponent(audience)}` : ''}`),
    composeCheck: (body) => call('POST', '/api/v1/compose/check', body, { timeout: POST_TIMEOUT_MS }),
    send: (body, signal) => call('POST', '/api/v1/send', body, { timeout: SEND_TIMEOUT_MS, signal }),
    sendDraftTurn: (body) => call('POST', '/api/v1/drafts/send', body, { timeout: SEND_TIMEOUT_MS }),
    loadDraft: (turnId) => call('POST', '/api/v1/drafts/load', { turn_id: turnId }, { timeout: POST_TIMEOUT_MS }),
    savePatchbayDraft: (body: PatchbayDraftSaveIn) =>
      call<PatchbayDraftSaveOut>('POST', '/api/v1/drafts/patchbay/save', body, { timeout: POST_TIMEOUT_MS }),
    pushPatchbayDraftToDesk: (turnId) =>
      call<PatchbayDraftPushOut>(
        'POST',
        '/api/v1/drafts/patchbay/push',
        { turn_id: turnId, idempotency_key: idem() },
        { timeout: POST_TIMEOUT_MS },
      ),
    discardPatchbayDraft: (turnId, deleteDeskCopy) =>
      call<PatchbayDraftDiscardOut>(
        'POST',
        '/api/v1/drafts/patchbay/discard',
        { turn_id: turnId, delete_desk_copy: deleteDeskCopy },
        { timeout: POST_TIMEOUT_MS },
      ),
    discardNativeChatDraft: (sessionId, turnId) =>
      call<NativeChatDraftDiscardOut>(
        'POST',
        `/api/v1/widget/staff/sessions/${encodeURIComponent(sessionId)}/drafts/${encodeURIComponent(turnId)}/discard`,
        undefined,
        { timeout: POST_TIMEOUT_MS },
      ),
    // Riley's chat turn is blocking (up to ~150 s on Riley's side).
    draftWithRiley: (body) => call('POST', '/api/v1/drafts/riley', body, { timeout: RILEY_DRAFT_TIMEOUT_MS }),
    logTime: (body) => call<TimeEntryOut>('POST', '/api/v1/time', body, { timeout: POST_TIMEOUT_MS }),
    timePrompt: (caseUuid) => call<TimeDefaultsOut>('GET', `/api/v1/cases/${caseUuid}/time-prompt`),
    async context(caseUuid) {
      const [ctx, coverage] = await Promise.all([
        // Riley being down must not hide coverage: its parts degrade on their own (rileyUnreachable).
        railGet<ApiContext>('Riley context', `/api/v1/cases/${caseUuid}/context`).catch((e: unknown) => {
          if (e instanceof UnreachableError) return null
          throw e
        }),
        // Coverage lines ride on the case detail (role-stripped by the serializer).
        railGet<ApiCase>('Conduit', `/api/v1/cases/${caseUuid}`).then((c) => c.coverage ?? []),
      ])
      if (!ctx || ctx.state === 'unreachable') {
        return { ...toContextView({ ok: true, state: 'ok' }, coverage), remotePath: 'unknown', rileyUnreachable: true }
      }
      return toContextView(ctx, coverage)
    },
    async workItems(caseUuid) {
      const rows = await railGet<ApiWorkItem[]>('Conduit', `/api/v1/cases/${caseUuid}/work-items`)
      return rows.map(toWorkItem)
    },
    async related(caseUuid) {
      const rows = await railGet<ApiCaseRow[]>('Patchbay', `/api/v1/cases/${caseUuid}/related`)
      return rows.map((r) => {
        const t = r.tickets?.find((x) => !x.tombstoned) ?? r.tickets?.[0]
        return {
          number: r.number,
          title: r.title,
          state: r.conversation_state.replace(/_/g, ' '),
          ticketNumber: t?.ticket_number,
          deskTicketId: t?.desk_ticket_id,
          scope: r.related_scope,
          scopeLabel: r.related_scope_label ?? undefined,
        }
      })
    },
    async workOrders(caseUuid) {
      // Native work orders (round 9): Patchbay owns them, so a Conduit outage no longer blanks this section.
      const page = await railGet<{ items: ApiWorkOrder[] }>(
        'Patchbay',
        `/api/v1/work-orders?case_id=${encodeURIComponent(caseUuid)}&limit=50`,
      )
      return page.items.map(toWorkOrder)
    },
    async obligations(caseUuid) {
      const page = await railGet<{ items: ApiObligation[] }>('Patchbay', `/api/v1/obligations?case_id=${caseUuid}&state=open`)
      return page.items.map(
        (o): ObligationView => ({
          id: o.id,
          kind: o.kind.replace(/_/g, ' '),
          owedBy: o.owed_by,
          party: o.owed_by_display ?? undefined,
          amount: moneyLabel(o.amount, o.currency),
          expects: o.expects ?? '',
          due: dayLabel(o.due_on),
        }),
      )
    },
    async time(caseUuid) {
      const rows = await railGet<TimeEntryOut[]>('Patchbay', `/api/v1/cases/${caseUuid}/time`)
      return mapTimeRows(rows)
    },
    timeReview,
    refreshTime: async (caseUuid) => {
      await call('POST', `/api/v1/cases/${encodeURIComponent(caseUuid)}/time/refresh`, {}, { timeout: POST_TIMEOUT_MS })
    },
    countDeskAutoTime: (caseUuid, deskTimeEntryId, body: DeskAutoCountIn) =>
      call<DeskAutoReviewOut>(
        'POST',
        `/api/v1/cases/${encodeURIComponent(caseUuid)}/time/desk-auto/${encodeURIComponent(deskTimeEntryId)}/count`,
        body,
        { timeout: POST_TIMEOUT_MS },
      ),
    ignoreDeskAutoTime: (caseUuid, deskTimeEntryId, reason) =>
      call<DeskAutoReviewOut>(
        'POST',
        `/api/v1/cases/${encodeURIComponent(caseUuid)}/time/desk-auto/${encodeURIComponent(deskTimeEntryId)}/ignore`,
        { reason: reason?.trim() || undefined },
        { timeout: POST_TIMEOUT_MS },
      ),
    candidates: (caseUuid, kind, q) =>
      call<CandidateView[]>('GET', `/api/v1/cases/${caseUuid}/candidates?kind=${kind}${q ? `&q=${encodeURIComponent(q)}` : ''}`),
    proposeLink: (caseUuid, ticketNumber) =>
      call<DraftCardOut>('POST', `/api/v1/cases/${caseUuid}/link`, { ticket_number: ticketNumber }, { timeout: POST_TIMEOUT_MS }),
    mergePreview: (targetCaseUuid, absorbCaseUuid) =>
      call<DeskMergePreview>('GET', `/api/v1/cases/${targetCaseUuid}/merge-preview?absorb_case_id=${encodeURIComponent(absorbCaseUuid)}`),
    proposeMerge: (targetCaseUuid, absorbCaseUuid, reason, alsoMergeDesk) =>
      call<DraftCardOut>(
        'POST',
        `/api/v1/cases/${targetCaseUuid}/merge`,
        {
          absorb_case_id: absorbCaseUuid,
          reason,
          ...(typeof alsoMergeDesk === 'boolean' ? { also_merge_desk: alsoMergeDesk } : {}),
        },
        { timeout: POST_TIMEOUT_MS },
      ),
    retryDeskMerge: (caseUuid, deskTicketIds) =>
      call<DeskMergeResult>(
        'POST',
        `/api/v1/cases/${caseUuid}/desk-merge`,
        { desk_ticket_ids: deskTicketIds },
        { timeout: POST_TIMEOUT_MS },
      ),
    removeCase: (caseUuid, body) => call<RemoveCaseOutput>('POST', `/api/v1/cases/${caseUuid}/remove`, body, { timeout: SEND_TIMEOUT_MS }),
    async flagCase(caseUuid, body) {
      await call('POST', `/api/v1/cases/${caseUuid}/removal-flags`, body, { timeout: POST_TIMEOUT_MS })
    },
    restoreDesk: (caseUuid) => call<RestoreDeskOutput>('POST', `/api/v1/cases/${caseUuid}/restore-desk`, {}, { timeout: SEND_TIMEOUT_MS }),
    async restoreCase(caseUuid) {
      await call('POST', `/api/v1/cases/${caseUuid}/restore`, {}, { timeout: POST_TIMEOUT_MS })
    },
    proposeMarkContractor: (caseUuid, body) =>
      call<DraftCardOut>('POST', `/api/v1/cases/${caseUuid}/people/mark-contractor`, body, { timeout: POST_TIMEOUT_MS }),
    contractorFirms: () => call<FirmOption[]>('GET', '/api/v1/portal/admin/firms'),
    vendors: () => call<VendorOption[]>('GET', '/api/v1/procurement/vendors'),
    async updateForm(caseUuid) {
      const [form, c] = await Promise.all([
        call<UpdateFormOut>('GET', `/api/v1/cases/${caseUuid}/update-form`),
        // Coverage lines only pre-fill the close-out picks: Conduit being down must not block the panel.
        call<ApiCase>('GET', `/api/v1/cases/${caseUuid}`).catch(() => null),
      ])
      const statusVocabulary = c?.status_vocabulary
      const statuses = statusVocabulary?.length
        ? statusVocabulary.map((status) => ({
            name: status.name,
            status_type: ({ open: 'Open', on_hold: 'On Hold', closed: 'Closed' } as const)[status.status_type],
          }))
        : form.statuses
      return {
        ...form,
        statuses,
        ...(c?.backend === 'native' && c.native_status ? { current_status: c.native_status } : {}),
        coverageLines: c?.coverage ?? [],
      }
    },
    suggestions: (caseUuid, intent) =>
      call<CaseSuggestionsOut>('GET', `/api/v1/cases/${caseUuid}/suggestions?intent=${encodeURIComponent(intent)}`),
    suggestionOutcomes: (caseUuid, outcomes) =>
      call<SuggestionOutcomesOut>('POST', `/api/v1/cases/${caseUuid}/suggestions/outcomes`, { outcomes }),
    resolutionDraft: (caseUuid, redraft) =>
      call<ResolutionDraftOut>('POST', `/api/v1/cases/${caseUuid}/update/resolution-draft`, { redraft }, { timeout: 170_000 }),
    resolutionCheck: (caseUuid, text) => call<ResolutionCheckOut>('POST', `/api/v1/cases/${caseUuid}/update/resolution-check`, { text }),
    submitUpdate: (caseUuid, body) => call<CaseUpdateOut>('POST', `/api/v1/cases/${caseUuid}/update`, body, { timeout: 120_000 }),
    finishCase: (caseUuid, body) => call<FinishCaseOut>('POST', `/api/v1/cases/${caseUuid}/finish-case`, body, { timeout: 120_000 }),
    closeConversation: (caseUuid, deskTicketId, body) =>
      call<CloseConversationOut>('POST', `/api/v1/cases/${caseUuid}/conversations/${encodeURIComponent(deskTicketId)}/close`, body, {
        timeout: POST_TIMEOUT_MS,
      }),
    removeConversationPane: (caseUuid, threadId) =>
      call<PaneActionOut>('POST', `/api/v1/cases/${caseUuid}/conversations/panes/${encodeURIComponent(threadId)}/remove`, undefined, {
        timeout: POST_TIMEOUT_MS,
      }),
    hideConversationPane: (caseUuid, threadId) =>
      call<PaneActionOut>('POST', `/api/v1/cases/${caseUuid}/conversations/panes/${encodeURIComponent(threadId)}/hide`, undefined, {
        timeout: POST_TIMEOUT_MS,
      }),
    setNextMove: (caseUuid, body) => call<NextMoveOut>('PUT', `/api/v1/cases/${caseUuid}/next-move`, body, { timeout: POST_TIMEOUT_MS }),
    clearNextMove: (caseUuid, body) =>
      call<NextMoveOut>('POST', `/api/v1/cases/${caseUuid}/next-move/clear`, body, { timeout: POST_TIMEOUT_MS }),
    completeNextMove: (moveId) =>
      call<NextMoveOut>('POST', `/api/v1/next-moves/${encodeURIComponent(moveId)}/done`, undefined, { timeout: POST_TIMEOUT_MS }),
    dismissNextMove: (moveId, body) =>
      call<NextMoveOut>('POST', `/api/v1/next-moves/${encodeURIComponent(moveId)}/dismiss`, body, { timeout: POST_TIMEOUT_MS }),
    events: (caseUuid) =>
      call<EventOut[]>(
        'GET',
        `/api/v1/cases/${caseUuid}/events?newest_first=true&limit=100&${ACTIVITY_KINDS.map((k) => `kind=${encodeURIComponent(k)}`).join('&')}`,
      ),
    async reopen(caseUuid, body) {
      await call(
        'POST',
        `/api/v1/cases/${caseUuid}/reopen`,
        { reason: body?.reason ?? null, courtesy: body?.courtesy ?? false },
        { timeout: POST_TIMEOUT_MS },
      )
    },
    correctClosedInbound: (caseUuid, body) => call('POST', `/api/v1/cases/${caseUuid}/closed-inbound`, body, { timeout: POST_TIMEOUT_MS }),
    async patchCase(caseUuid, body: CasePatchInput) {
      await call('PATCH', `/api/v1/cases/${caseUuid}`, body, { timeout: POST_TIMEOUT_MS })
    },
    phoneSystems: (caseUuid) => call('GET', `/api/v1/cases/${caseUuid}/phone-systems`),
    ticketEdit: (deskTicketId) => call<TicketEditView>('GET', `/api/v1/tickets/${encodeURIComponent(deskTicketId)}/edit`),
    async ticketReferences(deskTicketId, kind, q, accountId) {
      const account = accountId ? `&account_id=${encodeURIComponent(accountId)}` : ''
      const out = await call<TicketReferenceSearch>(
        'GET',
        `/api/v1/tickets/${encodeURIComponent(deskTicketId)}/references?kind=${kind}&q=${encodeURIComponent(q)}${account}`,
      )
      return out.options
    },
    async patchTicket(deskTicketId, body: TicketPatchInput) {
      const out = await call<TicketPatchResult>('PATCH', `/api/v1/tickets/${encodeURIComponent(deskTicketId)}`, body, {
        timeout: POST_TIMEOUT_MS,
      })
      return out.values
    },
    async searchCustomers(q) {
      const hits = await call<SearchHit[]>('GET', `/api/v1/search?q=${encodeURIComponent(q)}`)
      return hits.filter((h) => h.kind === 'customer')
    },
    async setCustomer(caseUuid, conduitCustomerId) {
      await call('POST', `/api/v1/cases/${caseUuid}/customer`, { conduit_customer_id: conduitCustomerId }, { timeout: POST_TIMEOUT_MS })
    },
    contactOptions: (caseUuid, deskTicketId) =>
      call<TicketContactOptions>(
        'GET',
        `/api/v1/cases/${caseUuid}/contact-options${deskTicketId ? `?desk_ticket_id=${encodeURIComponent(deskTicketId)}` : ''}`,
      ),
    proposeTicketContact: (caseUuid, deskTicketId, contact) =>
      call<DraftCardOut>(
        'POST',
        `/api/v1/cases/${caseUuid}/contact`,
        {
          desk_ticket_id: deskTicketId,
          contact_id: contact.contact_id,
          account_id: contact.account_id,
          contact_name: contact.name,
          contact_email: contact.email,
        },
        { timeout: POST_TIMEOUT_MS },
      ),
    addTicketContact: (caseUuid, body) =>
      call<AddTicketContactOut>('POST', `/api/v1/cases/${caseUuid}/identity/add-contact`, body, { timeout: POST_TIMEOUT_MS }),
    async searchCrm(caseUuid, q) {
      try {
        return await call<CrmAccountOut[]>('GET', `/api/v1/cases/${caseUuid}/crm-search?q=${encodeURIComponent(q)}`)
      } catch (e) {
        // "CRM is not connected in Conduit" is a configured-off state, not an outage and not an empty result.
        if (e instanceof ApiError && e.code === 'crm_not_enabled') throw new FeatureOffError('CRM')
        throw new UnreachableError('CRM', e instanceof Error ? e.message : undefined)
      }
    },
    proposeAdopt: (caseUuid, crmAccountId, crmAccountName) =>
      call<DraftCardOut>(
        'POST',
        `/api/v1/cases/${caseUuid}/adopt-customer`,
        { crm_account_id: crmAccountId, crm_account_name: crmAccountName },
        { timeout: POST_TIMEOUT_MS },
      ),
    undoAutoJoin: (caseUuid, deskTicketId) =>
      call<UndoJoinOut>(
        'POST',
        `/api/v1/cases/${caseUuid}/tickets/${encodeURIComponent(deskTicketId)}/undo-join`,
        {},
        { timeout: SEND_TIMEOUT_MS },
      ),
    proposeUnlink: (caseUuid, deskTicketId, reason, targetCaseUuid) =>
      call<DraftCardOut>(
        'POST',
        `/api/v1/cases/${caseUuid}/tickets/${encodeURIComponent(deskTicketId)}/unlink`,
        { reason: reason || null, target_case_id: targetCaseUuid ?? null },
        { timeout: POST_TIMEOUT_MS },
      ),
    proposeSplit: (caseUuid, deskTicketIds, title) =>
      call<DraftCardOut>(
        'POST',
        `/api/v1/cases/${caseUuid}/split`,
        { desk_ticket_ids: deskTicketIds, title: title || null },
        { timeout: POST_TIMEOUT_MS },
      ),
    proposePaneMerge: (caseUuid, sourceThreadId, targetThreadId) =>
      call<DraftCardOut>(
        'POST',
        `/api/v1/cases/${caseUuid}/panes/merge`,
        { source_thread_id: sourceThreadId, target_thread_id: targetThreadId },
        { timeout: POST_TIMEOUT_MS },
      ),
    proposePaneSplit: (caseUuid, body) =>
      call<DraftCardOut>('POST', `/api/v1/cases/${caseUuid}/panes/split`, body, { timeout: POST_TIMEOUT_MS }),
    undoPane: (caseUuid, actionId) =>
      call('POST', `/api/v1/cases/${caseUuid}/panes/undo`, { action_id: actionId }, { timeout: POST_TIMEOUT_MS }),
    proposeTurnCaseMove: (caseUuid, body) =>
      call<DraftCardOut>('POST', `/api/v1/cases/${caseUuid}/turn-moves`, body, { timeout: POST_TIMEOUT_MS }),
    undoTurnCaseMove: (caseUuid, actionId) =>
      call('POST', `/api/v1/cases/${caseUuid}/turn-moves/undo`, { action_id: actionId }, { timeout: POST_TIMEOUT_MS }),
    heartbeat: (caseUuid, kind, threadUuid) =>
      call<PresenceView[]>(
        'POST',
        `/api/v1/cases/${caseUuid}/presence`,
        { kind, thread_id: threadUuid ?? null },
        { timeout: POST_TIMEOUT_MS },
      ),
    presence: (caseUuid) => call<PresenceView[]>('GET', `/api/v1/cases/${caseUuid}/presence`),
    async leave(caseUuid) {
      await call('POST', `/api/v1/cases/${caseUuid}/presence/leave`, {}, { timeout: POST_TIMEOUT_MS })
    },
    async addObligation(body) {
      await call('POST', '/api/v1/obligations', body, { timeout: POST_TIMEOUT_MS })
    },
    async dismissObligation(obligationId, reason) {
      await call('POST', `/api/v1/obligations/${obligationId}/dismiss`, { reason: reason ?? null }, { timeout: POST_TIMEOUT_MS })
    },
    async reopenObligation(obligationId, reason) {
      await call('POST', `/api/v1/obligations/${obligationId}/reopen`, { reason }, { timeout: POST_TIMEOUT_MS })
    },
    async clearObligation(obligationId) {
      await call(
        'POST',
        `/api/v1/obligations/${obligationId}/clear`,
        { cleared_by_kind: 'manual', cleared_by_ref: 'case-page:done' },
        { timeout: POST_TIMEOUT_MS },
      )
    },
    pendingNotes: (caseUuid) => call<PendingNoteOut[]>('GET', `/api/v1/cases/${caseUuid}/notes/pending`),
    proposeConduitAttach: (caseUuid, turnId, attachmentId, target, note) =>
      call<DraftCardOut>(
        'POST',
        `/api/v1/cases/${caseUuid}/turns/${turnId}/attachments/${encodeURIComponent(attachmentId)}/conduit`,
        { target, note },
        { timeout: POST_TIMEOUT_MS },
      ),
  }
}

// ---------------------------------------------------------------------------------------------
// Contract -> view mapping. Kept beside the fetches so W2 can delete both together.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

type ApiCurrentNextMove = Omit<NextMoveOut, 'proposed_by' | 'state'> & {
  /** Case detail only exposes unfinished moves; optional while older fixture payloads are retired. */
  state?: Extract<NextMoveOut['state'], 'active' | 'proposed'>
  proposed_by?: NextMoveOut['proposed_by']
}

interface ApiCase {
  backend?: 'desk' | 'native'
  native_status?: 'new' | 'open' | 'waiting_customer' | 'on_hold' | 'resolved' | 'closed' | null
  status_vocabulary?: {
    name: string
    label: string
    status_type: 'open' | 'on_hold' | 'closed'
  }[]
  /** brands-visibility-queue `CaseBrandOut`; null = the case's brand is not resolved yet. */
  brand?: {
    id: string
    slug: string
    customer_facing_name: string
    support_from_address?: string | null
    our_domains?: string[]
  } | null
  placement_suggestion?: {
    desk_ticket_id: string
    ticket_number: string
    target_case_id: string
    target_case_number: string
    join_key?: string | null
  } | null
  id: string
  number: string
  title: string
  customer_display?: string | null
  contact_display?: string | null
  customer_provenance?: { source: string; ref?: string | null; state?: string } | null
  identity_state: CaseView['identity']
  identity_memory_line?: string | null
  identity_glance?: boolean
  site_address?: string | null
  site_address_provenance?: string | null
  tickets: {
    desk_ticket_id: string
    ticket_number: string
    role: string
    join_key?: string | null
    undo_until?: string | null
    deleted_in_desk?: boolean
    tombstoned?: boolean
    desk_status?: string | null
  }[]
  conversation_state: string
  /** Live lane from the mirrored turns. The cached conversation_state can lag a hand-off until the next ingest. */
  lane?: QueueLaneId | null
  ledger_state: string
  open_obligations_count?: number
  next_move?: ApiCurrentNextMove | null
  callback?: {
    obligation_id?: string | null
    caller_name?: string | null
    caller_number?: string | null
    duration?: string | null
    call_href?: string | null
    received_at: string
  } | null
  riley_call?: {
    turn_id: string
    state: 'live' | 'completed' | 'failed'
    outcome?: 'resolved' | 'transferred' | 'callback_booked' | 'voicemail' | 'caller_hung_up' | 'failed' | null
    caller_name?: string | null
    caller_number?: string | null
    language: 'en' | 'fr' | 'unknown'
    live_summary?: string | null
    started_at: string
    ended_at?: string | null
    recording_url?: string | null
  } | null
  symptom_first?: string | null
  symptom_latest?: string | null
  summary_line?: string | null
  summary_md?: string | null
  case_summary?: {
    headline: string
    problem: string
    tried: string[]
    status: string
    waiting_on: string
    next: string
    as_of: string
  } | null
  triage_needs_human?: string | null
  triage_safety?: string | null
  triage_category?: string | null
  coverage?: {
    domain: 'phone' | 'it' | 'macd'
    decision: string
    basis: string
    basis_detail?: string | null
  }[]
  conduit_customer_id?: string | null
  desk_account_id?: string | null
  account_customer_suggestion?: {
    conduit_customer_id: string
    customer_display?: string | null
  } | null
  customer_email_warning?: {
    desk_ticket_id: string
    ticket_number: string
    message: string
    no_customer_email_sent: boolean
  } | null
  contact_id?: string | null
  crm_account_id?: string | null
  conduit_phone_system_id?: string | null
  tags?: string[]
  customer_away_until?: string | null
  merged_into?: string | null
  all_tickets_deleted_in_desk?: boolean
  removed_at?: string | null
  removal_reason?: CaseView['removalReason'] | null
  duplicate_of_case_id?: string | null
  latest_customer_turn_id?: string | null
  latest_customer_desk_thread_id?: string | null
  reply_classification?: 'needs_reply' | 'no_reply_needed' | 'waiting_on_them' | null
  reply_classification_source?: 'human' | 'riley' | 'deterministic' | 'default' | null
  waiting_on_party?: string | null
  waiting_on_what?: string | null
}
interface ApiThread {
  id: string
  audience: 'customer' | 'contractor' | 'vendor' | 'internal' | 'unclassified'
  display_name_internal?: string | null
  party_ref?: string | null
  work_order_id?: string | null
  manual_override?: boolean
  manual_action_id?: string | null
  /** contractor | vendor | external_support | customer | internal (#90). */
  party_kind?: string | null
  desk_ticket_id?: string | null
  ticket_number?: string | null
  can_remove?: boolean
  parties?: ApiPaneParty[]
}
interface ApiPaneParty {
  email: string
  name?: string | null
  party_kind: 'external' | 'contractor'
  party_ref?: string | null
  party_name?: string | null
  has_work_order: boolean
}
interface ApiTurn {
  id: string
  desk_ticket_id?: string | null
  desk_thread_id?: string | null
  desk_comment_id?: string | null
  can_retract_internal_note?: boolean
  direction: 'in' | 'out' | 'draft' | 'note'
  delivery_state?: 'accepted' | 'delivered' | 'bounced' | 'complained' | 'uncertain' | null
  transport?: 'desk' | 'postmark' | 'chat' | 'phone' | 'portal' | 'internal' | null
  author_kind: string
  author_ref?: string | null
  author_name?: string | null
  author_email?: string | null
  entered_by_name?: string | null
  audience_source?: AudienceSource
  from_addr?: string | null
  staff_delivery_note?: string | null
  answered_mention?: boolean
  to?: string[]
  cc?: string[]
  /** Live Desk threads only. Mirrored turns do not store Bcc (#683). */
  bcc?: string[]
  sent_at?: string | null
  subject?: string | null
  source_version?: string | null
  text_clean?: string | null
  /** Sanitized email HTML (#209): inline images point at the attachment proxy. Null: render text_clean. */
  html_clean?: string | null
  is_unsent_draft?: boolean
  patchbay_draft?: boolean
  /** #511: on the viewer's own Patchbay draft, the Riley suggestion its composer adopted. */
  adopted_from_turn_id?: string | null
  desk_read_only_draft?: boolean
  also_in_desk?: boolean
  draft_intent?: 'close' | 'ask' | 'update' | 'resolve' | null
  is_auto_reply?: boolean
  auto_reply_until?: string | null
  /** #228: Riley's suggested reply, held in Patchbay only (no Desk draft). */
  riley_suggestion?: boolean
  highlights?: unknown[]
  attachments?: { id?: string | null; name?: string; size?: number | string | null; href?: string | null }[]
  /** zuid -> name for each Desk mention in text_clean (#113). */
  mentions?: Record<string, string>
  /** A recognised system message (Zoho Assist invite, booking, chat transcript; #205). */
  system_template?: SystemTemplate | null
  channel?: 'phone' | 'sent_mail' | 'zoho_mail' | null
  voicemail?: {
    caller_name?: string | null
    caller_number?: string | null
    duration?: string | null
    call_href?: string | null
    received_at: string
  } | null
  phone_call?: {
    turn_id: string
    state: 'live' | 'completed' | 'failed'
    outcome?: 'resolved' | 'transferred' | 'callback_booked' | 'voicemail' | 'caller_hung_up' | 'failed' | null
    caller_name?: string | null
    caller_number?: string | null
    language: 'en' | 'fr' | 'unknown'
    live_summary?: string | null
    started_at: string
    ended_at?: string | null
    recording_url?: string | null
    direction?: 'inbound' | 'outbound'
    source?: 'riley' | 'patchbay' | 'aftercalls'
  } | null
  time_entry_origin?: {
    id: string
    minutes: number
    domain?: 'phone' | 'it' | 'macd' | null
    billable: boolean
    covered_by_agreement?: boolean | null
    owner_name?: string | null
    entered_by_name?: string | null
  } | null
}
interface ApiThreadTurns {
  thread: ApiThread
  turns: ApiTurn[]
  as_of?: string | null
}
interface ApiCaseMirror {
  threads: ApiThreadTurns[]
  as_of?: string | null
}
interface ApiFreshness {
  checked: boolean
  stale?: boolean | null
  reason?: string | null
}
interface ApiContext {
  ok: boolean
  state?: 'ok' | 'no_context' | 'unreachable'
  stale?: boolean
  built_at?: string | null
  context?: Json | null
}
interface ApiWorkItem {
  conduit_work_item_id: string
  kind: string
  status: string
  title: string
  due_on?: string | null
  progress?: { done?: number; total?: number } | null
}
interface ApiCaseRow {
  number: string
  title: string
  conversation_state: string
  tickets?: { ticket_number: string; desk_ticket_id: string; tombstoned?: boolean }[]
  related_scope?: 'customer' | 'site' | 'management'
  related_scope_label?: string | null
}
interface ApiWorkOrder {
  id?: string | null
  contractor_id?: string | null
  display_number?: string | null
  case_scoped_label?: string | null
  status: WorkOrderView['status']
  assignee_kind?: 'contractor' | 'internal_tech'
  contractor_display?: string | null
  scheduled_start?: string | null
  time_precision?: string | null
  books_po_number?: string | null
  po_number?: string | null
  payment_status?: string | null
  tasks_done?: number
  tasks_total?: number
  /** Null when the visit hours are still unknown (#580). */
  minutes_total?: number | null
  /** "PO-00042" | "No PO (WO + bill)" (#100) */
  po_label?: string | null
  /** vendor invoice numbers allocated to this WO, e.g. "invoice 26207" */
  invoices?: string[]
}
interface ApiObligation {
  id: string
  kind: string
  owed_by: string
  owed_by_ref?: string | null
  /** #244: the firm's name for a contractor ref; the raw ref is never shown. */
  owed_by_display?: string | null
  expects?: string | null
  due_on?: string | null
  /** Money roles only (absent for tech). */
  amount?: string | number | null
  currency?: string | null
}

function moneyLabel(amount: string | number | null | undefined, currency: string | null | undefined): string | undefined {
  if (amount === null || amount === undefined || amount === '') return undefined
  const n = Number(amount)
  if (!Number.isFinite(n)) return undefined
  return `${currency ?? 'CAD'} ${n.toFixed(2)}`
}

function mapTimeRows(rows: TimeEntryOut[]): TimeRowView[] {
  return rows.map(
    (t): TimeRowView => ({
      id: t.id,
      who: t.owner_name ?? t.user?.name ?? (t.subject_kind === 'contractor' ? 'Contractor packet' : 'Unknown'),
      minutes: t.minutes,
      billable: t.billable,
      source: t.source,
      kind: t.kind ?? 'work',
      auto: t.is_desk_auto,
      sourceDeskTimeEntryId: t.source_desk_time_entry_id ?? (t.is_desk_auto ? (t.desk_time_entry_id ?? undefined) : undefined),
      deskAutoReviewState: t.desk_auto_review_state ?? undefined,
      deskAutoActionable: t.desk_auto_actionable,
      noTime: t.is_no_time ? (t.no_time_reason ?? 'no time') : undefined,
      pending: t.desk_sync_state === 'pending_token',
      enteredBy: t.entered_by_name ?? undefined,
      note: t.note?.trim() || undefined,
      at: t.executed_at,
    }),
  )
}

function dayLabel(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(`${iso}T12:00:00`)
  return Number.isNaN(d.valueOf()) ? iso : formatWhen(d.toISOString(), { style: 'date' })
}

const SITE_SOURCE: Record<string, string> = {
  conduit: 'Conduit customer',
  crm: 'CRM',
  email_signature: 'email signature',
  ticket: 'ticket',
  manual: 'set by hand',
  desk: 'Desk account',
}
const siteSource = (p?: string | null) => (p ? (SITE_SOURCE[p] ?? p) : '')

const ROLE_LABEL: Record<string, string> = {
  primary: 'service',
  sales_scope: 'scope',
  signed_quote: 'signed quote',
  vendor_invoice: 'vendor invoice',
}

const fmt = (iso?: string | null) => (iso ? formatWhen(iso, { style: 'absolute' }) : '')

const toHtml = (text: string) =>
  text
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}</p>`)
    .join('')

const KIND_LABEL: Record<string, string> = { customer: 'Customer contact', staff: 'Ewee staff', contractor: 'Contractor', riley: 'Riley' }

/** The author by name (the API resolves it from the Desk snapshot, the ticket contact or the user), then by address. */
function authorOf(t: ApiTurn): { name: string; email?: string; riley: boolean } {
  const email = t.author_email ?? bareAddress(t.from_addr) ?? undefined
  const name = t.author_name ?? email ?? bareAddress(t.author_ref) ?? KIND_LABEL[t.author_kind] ?? t.author_kind
  return { name, email: email && email !== name ? email : undefined, riley: t.author_kind === 'riley' }
}

// Mail-gateway banners the customer's own system highlights ("CAUTION: External email…"). They are not answers.
const BANNER =
  /^\W*(caution|warning|external sender|important disclaimer)\b|originated from outside|sent from outside|external e-?mail|external sender|(?:you|some people who received this message)\s+don.t often get email from/i

/** An Outlook reaction email, stored by ingest as a marker in `highlights` (#174). */
interface ReactionMark {
  kind: 'reaction'
  reaction?: string
  by?: string | null
  target_turn_id?: string | null
  target_desk_thread_id?: string | null
}

function reactionOf(t: ApiTurn): ReactionMark | null {
  const h = (t.highlights ?? []).find(
    (x): x is ReactionMark => typeof x === 'object' && x !== null && (x as { kind?: unknown }).kind === 'reaction',
  )
  return h ?? null
}

/**
 * Pull reaction emails out of a pane's turns: each becomes a marker on the turn it reacted to (by id, then Desk
 * thread id, then the latest outbound before it). One with no outbound to sit on stays a one-line turn.
 */
function splitReactions(turns: ApiTurn[]): {
  turns: ApiTurn[]
  marks: Map<string, { reaction: string; by: string; at?: string }[]>
  orphans: Map<string, string>
} {
  const marks = new Map<string, { reaction: string; by: string; at?: string }[]>()
  const orphans = new Map<string, string>()
  const kept: ApiTurn[] = []
  const reactions: { turn: ApiTurn; mark: ReactionMark }[] = []
  for (const t of turns) {
    const mark = t.direction === 'in' ? reactionOf(t) : null
    if (mark) reactions.push({ turn: t, mark })
    else kept.push(t)
  }
  for (const { turn, mark } of reactions) {
    const before = (o: ApiTurn) => !turn.sent_at || !o.sent_at || o.sent_at <= turn.sent_at
    const target =
      kept.find((o) => mark.target_turn_id && o.id === mark.target_turn_id) ??
      kept.find((o) => mark.target_desk_thread_id && o.desk_thread_id === mark.target_desk_thread_id) ??
      [...kept].reverse().find((o) => o.direction === 'out' && !o.is_unsent_draft && before(o))
    const by = mark.by || turn.author_name || 'The customer'
    if (!target) {
      kept.push(turn)
      orphans.set(turn.id, `${by} reacted to one of our messages`)
      continue
    }
    const list = marks.get(target.id) ?? []
    list.push({ reaction: mark.reaction ?? 'unknown', by, at: fmt(turn.sent_at) || undefined })
    marks.set(target.id, list)
  }
  if (orphans.size) kept.sort((a, b) => (a.sent_at ?? '').localeCompare(b.sent_at ?? ''))
  return { turns: kept, marks, orphans }
}

/**
 * Highlighted text the customer typed into our quoted email: strings only, no gateway banners, no repeats. Only an
 * inbound turn can carry one (an outbound turn's highlights are never the customer answering), and a system template
 * turn (a Zoho Assist invite etc., #205) carries none.
 */
function inlineAnswers(highlights?: unknown[], turn?: Pick<ApiTurn, 'direction' | 'system_template'>): string[] {
  if (turn && (turn.system_template || turn.direction !== 'in')) return []
  const seen = new Set<string>()
  return (highlights ?? []).filter((h): h is string => {
    if (typeof h !== 'string' || !h.trim() || BANNER.test(h.trim())) return false
    const key = h.trim().toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** The party's name from a turn it wrote, else its address. */
function partyName(partyRef: string, turns: ApiTurn[]): string {
  const ref = partyRef.toLowerCase()
  const own = turns.find((t) => (t.author_email ?? bareAddress(t.from_addr)) === ref && t.author_name)
  return own?.author_name ?? partyRef
}

/** "First: … Newest: …", or the one text when the newest turn says the same as the first. */
const OUR_DOMAIN = /@(e-wee\.com|[a-z0-9-]+\.zohodesk\.com)$/i

/** Who is on a pane besides Ewee: inbound writers and our sends' To, newest first, named from their own turns. */
function panepeople(
  turns: ApiTurn[],
  names: Map<string, string> = new Map(),
  parties: ApiPaneParty[] = [],
  contact?: { email?: string | null; id?: string | null },
): NonNullable<ThreadView['people']> {
  const known = new Map(parties.map((p) => [p.email.toLowerCase(), p]))
  const out = new Map<string, NonNullable<ThreadView['people']>[number]>()
  for (const t of [...turns].reverse()) {
    const addrs =
      t.direction === 'in'
        ? [t.author_email ?? bareAddress(t.from_addr), ...(t.cc ?? []).map(bareAddress)]
        : t.direction === 'note'
          ? []
          : [...(t.to ?? []).map(bareAddress), ...(t.cc ?? []).map(bareAddress)]
    for (const a of addrs) {
      if (!a || OUR_DOMAIN.test(a) || out.has(a)) continue
      const own = partyName(a, turns)
      const party = known.get(a)
      out.set(a, {
        email: a,
        name: party?.name ?? (own !== a ? own : names.get(a)),
        partyKind: party?.party_kind,
        partyRef: party?.party_ref ?? undefined,
        partyName: party?.party_name ?? undefined,
        hasWorkOrder: party?.has_work_order,
      })
    }
  }
  for (const party of parties) {
    const email = party.email.toLowerCase()
    if (OUR_DOMAIN.test(email) || out.has(email)) continue
    out.set(email, {
      email,
      name: party.name ?? names.get(email),
      partyKind: party.party_kind,
      partyRef: party.party_ref ?? undefined,
      partyName: party.party_name ?? undefined,
      hasWorkOrder: party.has_work_order,
    })
  }
  const rows = [...out.values()]
  const contactEmail = contact?.email?.trim().toLowerCase()
  if (!contactEmail) return rows
  const marked = rows.map((person) =>
    person.email.toLowerCase() === contactEmail
      ? { ...person, ticketContact: true, ...(contact?.id ? { contactId: contact.id } : {}) }
      : person,
  )
  return orderPanePeople(marked)
}

/** Legacy #100 rows may hold one customer-side pane per sender. Render the Desk conversation as one pane now; the
 * dry-run #412 repair removes those duplicate rows after the coordinator reviews its count. */
function coalesceCustomerPanes(rows: ApiThreadTurns[]): ApiThreadTurns[] {
  // A manually split Customer conversation is deliberately separate (#412 part 2); only legacy automatic panes
  // coalesce while the repair rollout is still able to encounter them.
  const customer = rows.filter((row) => row.thread.audience === 'customer' && !row.thread.desk_ticket_id && !row.thread.manual_override)
  if (customer.length <= 1) return rows
  const canonical = customer.find((row) => !row.thread.party_ref) ?? customer[0]
  if (!canonical) return rows
  const parties = new Map<string, ApiPaneParty>()
  for (const row of customer) {
    for (const party of row.thread.parties ?? []) {
      const key = party.email.toLowerCase()
      const prior = parties.get(key)
      if (!prior || party.party_kind === 'contractor') parties.set(key, party)
    }
  }
  const turns = customer.flatMap((row) => row.turns).sort((a, b) => (a.sent_at ?? '').localeCompare(b.sent_at ?? ''))
  const merged: ApiThreadTurns = {
    ...canonical,
    thread: { ...canonical.thread, party_ref: null, parties: [...parties.values()] },
    turns,
  }
  return rows.map((row) => (row === canonical ? merged : row)).filter((row) => row === merged || !customer.includes(row))
}

function symptomLine(rawFirst?: string | null, rawLatest?: string | null): string {
  // Never a raw Desk mention or Riley highlight marker in the one-line symptom (#113).
  const first = rawFirst ? plainMarkup(rawFirst) : rawFirst
  const latest = rawLatest ? plainMarkup(rawLatest) : rawLatest
  const same = (first ?? '').replace(/\s+/g, ' ').trim() === (latest ?? '').replace(/\s+/g, ' ').trim()
  if (first && (!latest || same)) return first
  return [first && `First: ${first}`, latest && `Newest: ${latest}`].filter(Boolean).join(' ')
}

const GREETING = /^(hi|hello|hey|good (morning|afternoon|evening)|dear|greetings|bonjour|salut)\b[^.!?\n]{0,40}?[,!:-]\s*/i
// A sign-off or a mail client's footer ends the customer's words; whatever follows is signature junk.
const SIGN_OFF = new RegExp(
  `${[
    '(?:^|\\s)(?:--+\\s',
    'thanks?(?: you| so much)?[,!.]',
    'thank you[,!.]',
    '(?:kind |best |warm )?regards[,.!]',
    'best[,!]',
    'cheers[,!]',
    'sincerely[,.]',
    'sent from my\\b',
    'get outlook for\\b',
    // Legal footers and gateway banners (C-00050 showed confidentiality notices in the header).
    'confidential(?:ity)?(?: notice| warning| statement)?:',
    'confidentiality (?:notice|warning|statement)\\b',
    'privileged (?:and|&) confidential\\b',
    'disclaimer:',
    'this (?:e-?mail|message|communication|transmission)(?:,? (?:and|including|together with) any (?:attachments?|files?)(?: transmitted)?(?: (?:to|with) it)?)?,? (?:(?:is|are|may be) (?:intended|confidential|privileged|for the (?:sole|exclusive))|contains? (?:confidential|privileged))\\b',
    'the information (?:contained )?in this (?:e-?mail|message|communication)\\b',
    'if you (?:are not|have received this) (?:the intended recipient|in error)\\b',
    'caution: this e-?mail originated\\b',
    'please consider the environment\\b',
  ].join('|')})`,
  'i',
)
const HEADLINE_CHARS = 220
const EMPTY_SUMMARY_LINES = new Set(['(no subject)', 'no subject', 'none', 'n/a'])

/**
 * The customer's first line for the case header (#263): the first turn's opening words without the greeting or the
 * signature, cut at a sentence end near HEADLINE_CHARS. Never the "First: … Newest: …" join.
 */
function firstCustomerLine(raw?: string | null): string {
  if (!raw) return ''
  let text = plainMarkup(raw)
    .replace(/\r/g, '')
    .replace(/^(?:\[C-\d{5,}\]\s*)?(?:(?:re|fw|fwd):\s*)*/i, '')
  // The first paragraph that is not just a greeting.
  const paras = text
    .split(/\n\s*\n|\n/)
    .map((p) => p.trim())
    .filter(Boolean)
  const body = paras.filter((p) => p.replace(GREETING, '').trim().length > 0)
  text = (body.length ? body : paras).join(' ')
  text = stripFooter(text.replace(GREETING, ''))
  // "Hello, our PIN stopped" reads as "Our PIN stopped" once the greeting is gone.
  const line = clampLine(text.charAt(0).toUpperCase() + text.slice(1))
  return line.length < 3 || EMPTY_SUMMARY_LINES.has(line.toLowerCase()) ? '' : line
}

/** The customer's words only: everything from the first sign-off, signature or legal footer on is dropped. */
function stripFooter(text: string): string {
  const end = SIGN_OFF.exec(text)
  return (end && end.index > 0 ? text.slice(0, end.index) : text).trim()
}

/** One line of at most HEADLINE_CHARS, cut at a sentence end when there is one. */
function clampLine(raw: string): string {
  const text = raw.replace(/\s+/g, ' ').trim()
  if (text.length <= HEADLINE_CHARS) return text
  const cut = text.slice(0, HEADLINE_CHARS)
  const stop = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('? '), cut.lastIndexOf('! '))
  return stop > 60 ? cut.slice(0, stop + 1) : `${cut.replace(/\s+\S*$/, '')}…`
}

/** The header's short summary is only Riley's structured headline (#382). */
function headline(c: Pick<ApiCase, 'case_summary'>): {
  text: string
  source?: 'riley'
} {
  // Defence in depth: older Riley senders may not yet have applied their footer cleaner.
  const riley = clampLine(stripFooter(plainMarkup(c.case_summary?.headline ?? '')))
  if (riley) return { text: riley, source: 'riley' }
  return { text: '' }
}

function structuredSummary(raw: ApiCase['case_summary']): CaseView['caseSummary'] {
  if (!raw) return undefined
  const clean = (value: string) => stripFooter(plainMarkup(value)).trim()
  const headline = clampLine(clean(raw.headline))
  if (!headline) return undefined
  return {
    headline,
    problem: clean(raw.problem),
    tried: raw.tried.map(clean).filter(Boolean),
    status: clean(raw.status),
    waitingOn: clean(raw.waiting_on),
    next: clean(raw.next),
    asOf: raw.as_of,
  }
}

/** #727: a `noreply@` / `alerts@` style sender (same shape as the API's `is_robot_sender`). */
const ROBOT_SENDER = /^(?:no[-_.]?reply|do[-_.]?not[-_.]?reply|mailer-daemon|postmaster@|notifications?@|alerts?@|bounce)/i
function isRobotSender(value?: string | null): boolean {
  return ROBOT_SENDER.test(bareAddress(value) ?? '')
}

function bareAddress(value?: string | null): string | null {
  return replyAddress(value)
}

/** Header labels: a known name when the pane has one, otherwise the address Desk stored. */
function labelAddresses(emails: string[] | undefined, names: Map<string, string>): string | undefined {
  if (!emails?.length) return undefined
  return emails
    .map((raw) => {
      const email = replyAddress(raw) ?? raw
      return names.get(email) ?? raw
    })
    .join(', ')
}

/** Patchbay's expectation follows the live lane. It is deliberately independent of the primary ticket's Desk status (#790). */
function patchbayExpectationFromCase(c: ApiCase): CaseView['deskState'] {
  if (c.lane === 'tech_report') return 'waiting_technician'
  if (c.conversation_state === 'closed') return 'closed'
  if (c.lane === 'reply_owed' || c.lane === 'waiting_customer' || c.lane === 'waiting_contractor' || c.lane === 'scheduled') {
    return c.lane
  }
  if (c.lane === 'action_owed' || c.lane === 'later' || c.lane === 'signals') return 'open'
  return (c.conversation_state === 'action_owed' ? 'open' : c.conversation_state) as CaseView['deskState']
}

function draftTime(turn: { sent_at?: string | null; source_version?: string | null }): number {
  const raw = turn.sent_at || turn.source_version
  if (!raw) return 0
  const parsed = Date.parse(raw)
  return Number.isNaN(parsed) ? 0 : parsed
}

/** Which unsent draft seeds the reply box.

 * The viewer's Patchbay draft wins, then a person's Desk draft. When the only Desk draft is Riley's and a
 * newer suggestion exists, the box stays empty so "Use Riley's suggestion" remains (#1195, #511). A lone
 * Riley draft or suggestion still seeds the box.
 */
function pickStagedDraft<
  T extends {
    is_unsent_draft?: boolean | null
    patchbay_draft?: boolean | null
    desk_thread_id?: string | null
    riley_suggestion?: boolean | null
    author_kind?: string | null
    desk_read_only_draft?: boolean | null
    sent_at?: string | null
    source_version?: string | null
  },
>(turns: T[]): T | undefined {
  const newest = (pred: (turn: T) => boolean) => [...turns].reverse().find(pred)
  const own = newest((turn) => Boolean(turn.is_unsent_draft && turn.patchbay_draft))
  if (own) return own
  const person = newest((turn) =>
    Boolean(turn.is_unsent_draft && turn.desk_thread_id && !turn.patchbay_draft && !turn.riley_suggestion && turn.author_kind !== 'riley'),
  )
  if (person) return person
  const suggestion = newest((turn) => Boolean(turn.is_unsent_draft && turn.riley_suggestion))
  const rileyDesk = newest((turn) =>
    Boolean(turn.is_unsent_draft && turn.desk_thread_id && !turn.patchbay_draft && !turn.riley_suggestion && turn.author_kind === 'riley'),
  )
  if (rileyDesk && suggestion && draftTime(suggestion) >= draftTime(rileyDesk)) return undefined
  if (rileyDesk && suggestion) return rileyDesk
  return newest((turn) => Boolean(turn.is_unsent_draft && !turn.desk_read_only_draft && turn.author_kind === 'riley'))
}

function toCaseView(
  c: ApiCase,
  threads: ApiThreadTurns[],
  opts: { source: 'mirror' | 'live'; asOf?: string | null } = { source: 'live' },
): CaseView {
  threads = coalesceCustomerPanes(threads)
  const liveTickets = c.tickets.filter((t) => !t.tombstoned && t.role !== 'merged_in_desk')
  const primary = liveTickets.find((t) => t.role === 'primary' || t.role === 'service') ?? liveTickets[0]
  // Unclassified turns (Conduit identify was unavailable at ingest) render greyed in Customer with a classify control;
  // there is no composer for them, so nothing is ever sent to that thread until a person classifies it (§7.2 rule 4).
  const unclassified: UnclassifiedTurnView[] = threads
    .filter((t) => t.thread.audience === 'unclassified')
    .flatMap(({ turns }) =>
      turns.map((t) => ({
        id: t.id,
        author: authorOf(t),
        at: fmt(t.sent_at),
        body: t.text_clean ?? '',
        mentions: t.mentions ?? {},
        direction: t.direction === 'in' ? ('inbound' as const) : t.direction === 'note' ? ('note' as const) : ('outbound' as const),
      })),
    )
  // Names people signed their own turns with, across every pane (our "Hey Paul" To in Customer names Paul Osborne).
  const names = new Map<string, string>()
  for (const { turns } of threads)
    for (const t of turns) {
      const e = t.author_email ?? bareAddress(t.from_addr)
      if (e && t.author_name && t.author_name !== e && !names.has(e)) names.set(e, t.author_name)
    }
  const contactBare = bareAddress(c.contact_display)
  const contactNamed = c.contact_display?.match(/^(.*?)\s*<[^>]+>$/)
  const contactName = contactNamed?.[1]?.trim().replace(/^"|"$/g, '')
  if (contactBare && contactName && !names.has(contactBare)) names.set(contactBare, contactName)
  // Delivery failures can arrive on a separate robot ticket attached to the case.  Apply that evidence to every
  // customer-pane suggestion instead of limiting it to the NDR's own thread (#1528).
  const rejectedAddresses = rejectedRecipientAddresses(threads.flatMap(({ turns }) => turns))
  const views: ThreadView[] = threads
    .filter((t) => t.thread.audience !== 'unclassified')
    .map(({ thread, turns, as_of }) => {
      const audience: ThreadView['audience'] =
        thread.audience === 'vendor' ? 'contractor' : thread.audience === 'unclassified' ? 'internal' : thread.audience
      const shown = splitReactions(turns)
      // The viewer's Patchbay draft wins, then a person's Desk draft (#1034). Riley's Desk draft does not
      // pre-fill the box when a newer suggestion exists (#1195); that card keeps "Use Riley's suggestion".
      const stagedDraft = pickStagedDraft(turns)
      const contactEmail = thread.party_ref?.includes('@')
        ? thread.party_ref
        : thread.audience === 'customer'
          ? bareAddress(c.contact_display)
          : null
      const replyContext = { audience: thread.audience, contactEmail, ourDomains: c.brand?.our_domains, rejectedAddresses }
      const recipients = replyAllRecipients(turns, replyContext)
      const recipient = (email: string) => ({ email, name: names.get(email) })
      // A second customer-side thread (a colleague, another address at the customer) is named after its party, so
      // the tabs never read "Customer · Customer · Customer" (it shows "Pamela Perrier customer").
      const label =
        audience === 'customer'
          ? thread.manual_override && thread.display_name_internal
            ? thread.display_name_internal
            : thread.party_ref
              ? partyName(thread.party_ref, turns)
              : 'Customer'
          : audience === 'internal'
            ? 'Internal'
            : (thread.display_name_internal ?? 'Contractor')
      return {
        id: thread.id,
        uuid: thread.id,
        label,
        // a contractor/vendor pane names the person on it too ("Positel · Paul Osborne"): internal-only labels
        sublabel:
          audience === 'customer' && thread.party_ref
            ? 'customer'
            : audience === 'contractor'
              ? panepeople(turns, names, thread.parties)[0]?.name
              : undefined,
        audience,
        contractorId: thread.audience === 'contractor' ? (thread.party_ref ?? undefined) : undefined,
        partyAudience: thread.audience === 'vendor' ? 'vendor' : thread.audience === 'contractor' ? 'contractor' : undefined,
        partyRef: thread.party_ref ?? undefined,
        external: Boolean(thread.desk_ticket_id),
        manualOverride: Boolean(thread.manual_override),
        manualActionId: thread.manual_action_id ?? undefined,
        workOrderId: thread.work_order_id ?? undefined,
        deskTicketId: thread.desk_ticket_id ?? undefined,
        ticketNumber: thread.ticket_number ? String(thread.ticket_number) : undefined,
        canRemove: thread.can_remove,
        people: panepeople(
          turns,
          names,
          thread.parties,
          audience === 'customer' ? { email: bareAddress(c.contact_display), id: c.contact_id } : undefined,
        ),
        turns: shown.turns.map((t) => {
          const through = turns.slice(0, turns.indexOf(t) + 1)
          const addressed = replyAllForTurn(t, through, replyContext)
          // Sent turns show the addresses that actually went out. A suggestion shows the reply-all that Send as is
          // will use, so the header matches the send (#683).
          const planned = Boolean(t.riley_suggestion || (t.is_unsent_draft && t.author_kind === 'riley'))
          return {
            id: t.id,
            sourceVersion: t.source_version ?? t.sent_at ?? as_of ?? undefined,
            author: authorOf(t),
            authorKind: t.author_kind as 'customer' | 'contractor' | 'vendor' | 'staff' | 'riley',
            direction:
              t.direction === 'note'
                ? ('note' as const)
                : t.direction === 'in' && t.author_kind !== 'staff'
                  ? ('inbound' as const)
                  : ('outbound' as const),
            deliveryState: t.delivery_state ?? undefined,
            transport: t.transport ?? undefined,
            staffDeliveryNote: t.staff_delivery_note ?? undefined,
            answeredMention: thread.audience === 'internal' && t.answered_mention === true ? true : undefined,
            publicNote: t.direction === 'note' && thread.audience === 'customer' && t.audience_source === 'to_party',
            to: labelAddresses(planned ? addressed.to : t.to, names),
            cc: labelAddresses(planned ? addressed.cc : t.cc, names),
            bcc: planned ? undefined : labelAddresses(t.bcc, names),
            replyTo: addressed.to.map(recipient),
            replyCc: addressed.cc.map(recipient),
            at: !t.sent_at && as_of ? `as of ${fmt(as_of)}` : fmt(t.sent_at),
            sentAt: t.sent_at ?? undefined,
            autoReply: t.is_auto_reply
              ? {
                  label: `Automatic reply${t.auto_reply_until ? ` · back ${dayLabel(t.auto_reply_until)}` : ''}`,
                  returnDate: t.auto_reply_until ?? undefined,
                }
              : undefined,
            automated: t.direction === 'in' && isRobotSender(t.from_addr) ? true : undefined,
            body: shown.orphans.get(t.id) ?? t.text_clean ?? '',
            enteredBy: t.entered_by_name ?? undefined,
            html: shown.orphans.has(t.id) ? undefined : t.html_clean || undefined,
            draft: t.is_unsent_draft ? { savedAt: fmt(t.sent_at) || 'unsent' } : undefined,
            inlineAnswers: inlineAnswers(t.highlights, t),
            systemTemplate: t.system_template ?? undefined,
            mailSource: t.channel === 'sent_mail' || t.channel === 'zoho_mail' ? t.channel : undefined,
            reactions: shown.marks.get(t.id),
            mentions: t.mentions ?? {},
            deskTicketId: t.desk_ticket_id ?? undefined,
            deskThreadId: t.desk_thread_id ?? undefined,
            deskCommentId: t.desk_comment_id ?? undefined,
            canRetractInternalNote: Boolean(t.can_retract_internal_note),
            rileyDraft: Boolean(t.is_unsent_draft && t.author_kind === 'riley'),
            rileySuggestion: Boolean(t.riley_suggestion),
            patchbayDraft: Boolean(t.patchbay_draft),
            adoptedFromTurnId: t.adopted_from_turn_id ?? undefined,
            deskReadOnlyDraft: Boolean(t.desk_read_only_draft),
            ...(t.is_unsent_draft ? { draftTo: t.to ?? [], draftCc: t.cc ?? [] } : {}),
            alsoInDesk: Boolean(t.also_in_desk),
            draftIntent: t.draft_intent ?? undefined,
            timeEntry: t.time_entry_origin
              ? {
                  id: t.time_entry_origin.id,
                  minutes: t.time_entry_origin.minutes,
                  domain: t.time_entry_origin.domain,
                  billable: t.time_entry_origin.billable,
                  covered: t.time_entry_origin.covered_by_agreement,
                  ownerName: t.time_entry_origin.owner_name,
                  enteredByName: t.time_entry_origin.entered_by_name,
                }
              : undefined,
            files: (t.attachments ?? []).map((a) => ({
              id: a.id ?? null,
              name: a.name || 'attachment',
              size: a.size ?? null,
              ...(typeof a.href === 'string' ? { href: a.href } : {}),
            })),
            voicemail: t.voicemail
              ? {
                  callerName: t.voicemail.caller_name ?? undefined,
                  callerNumber: t.voicemail.caller_number ?? undefined,
                  duration: t.voicemail.duration ?? undefined,
                  callHref: t.voicemail.call_href ?? undefined,
                }
              : undefined,
            phoneCall: t.phone_call
              ? {
                  state: t.phone_call.state,
                  outcome: t.phone_call.outcome ?? undefined,
                  callerName: t.phone_call.caller_name ?? undefined,
                  callerNumber: t.phone_call.source === 'aftercalls' ? undefined : (t.phone_call.caller_number ?? undefined),
                  language: t.phone_call.language,
                  liveSummary: t.phone_call.live_summary ?? undefined,
                  startedAt: fmt(t.phone_call.started_at),
                  endedAt: t.phone_call.ended_at ? fmt(t.phone_call.ended_at) : undefined,
                  recordingUrl: t.phone_call.source === 'aftercalls' ? undefined : (t.phone_call.recording_url ?? undefined),
                  direction: t.phone_call.direction ?? 'inbound',
                  source: t.phone_call.source,
                }
              : undefined,
          }
        }),
        composer: {
          // This is already reply-all safe before the advisory guard answers (#227). The guard can advise, but a
          // person's send preserves these bytes and recipients, so our own staff/intake addresses must never be To.
          to: recipients.to.map(recipient),
          cc: recipients.cc.map(recipient),
          removed: recipients.removed.map((item) => ({ email: item.address, reason: item.reason })),
          draftHtml: stagedDraft ? stagedDraft.html_clean || (stagedDraft.text_clean ? toHtml(stagedDraft.text_clean) : '') : '',
          // #511: which draft turn the reply box is seeded with, so that turn renders in one place only.
          draftTurnId: stagedDraft?.id,
          guard: { state: 'ok', summary: '' },
        },
        rileyDraft: Boolean(stagedDraft?.text_clean && stagedDraft.author_kind === 'riley'),
      }
    })
  applyExternalPanes(views, threads)
  // #1037: a ticketless placeholder is not a second tab once the firm's ticketed pane is here, and an extra
  // empty Internal pane is not a tab. A lone empty contractor stays; the API decides live links.
  const shown = omitLeftoverPanes(views)
  // Staff notes always have a home: the Internal tab exists even before the first private comment is mirrored.
  if (!shown.some((v) => v.audience === 'internal')) {
    shown.push({ id: 'internal', uuid: '', label: 'Internal', audience: 'internal', turns: [], rileyDraft: false })
  }
  const asOf = opts.asOf ?? threads.find((t) => t.as_of)?.as_of
  return {
    id: c.number,
    uuid: c.id,
    backend: c.backend ?? 'desk',
    nativeStatus: c.native_status ?? undefined,
    statusVocabulary: c.status_vocabulary?.map((status) => ({ ...status })),
    brand: c.brand
      ? {
          slug: c.brand.slug,
          customerFacingName: c.brand.customer_facing_name,
          supportFromAddress: c.brand.support_from_address,
          ourDomains: c.brand.our_domains ?? [],
        }
      : null,
    latestCustomerTurnId: c.latest_customer_turn_id ?? undefined,
    latestCustomerDeskThreadId: c.latest_customer_desk_thread_id ?? undefined,
    replyClassification: c.reply_classification ?? undefined,
    replyClassificationSource: c.reply_classification_source ?? undefined,
    waitingOnParty: c.waiting_on_party ?? undefined,
    waitingOnWhat: c.waiting_on_what ?? undefined,
    title: c.title,
    deskTicketId: primary?.desk_ticket_id || undefined,
    deskStatus: primary?.desk_status || undefined,
    // The account when resolved, else the Desk contact (never a stand-in name when a contact is known).
    customer: c.customer_display ?? (c.contact_display ? contactLabel(c.contact_display) : 'Customer not resolved'),
    customerAwayUntil: c.customer_away_until ?? undefined,
    customerSource: c.customer_provenance
      ? `${c.customer_provenance.source}${c.customer_provenance.ref ? ` ${c.customer_provenance.ref}` : ''}`
      : 'unresolved',
    identity: c.identity_state,
    identityMemoryLine: c.identity_memory_line ?? undefined,
    identityGlance: Boolean(c.identity_glance),
    // No site known: no line at all (the header used to print the word "unknown").
    address: c.site_address
      ? { line: c.site_address, source: siteSource(c.site_address_provenance), href: mapsUrl(c.site_address) }
      : undefined,
    tickets: c.tickets.map((t) => ({
      number: Number(t.ticket_number),
      deskTicketId: t.desk_ticket_id,
      deskStatus: t.desk_status || undefined,
      role: ROLE_LABEL[t.role] ?? t.role,
      joinKey: t.join_key ?? undefined,
      autoJoinUndo: Boolean(t.undo_until && new Date(t.undo_until) > new Date()),
      undoUntil: t.undo_until ?? undefined,
    })),
    placementSuggestion: c.placement_suggestion
      ? {
          deskTicketId: c.placement_suggestion.desk_ticket_id,
          ticketNumber: c.placement_suggestion.ticket_number,
          targetCaseId: c.placement_suggestion.target_case_id,
          targetCaseNumber: c.placement_suggestion.target_case_number,
          joinKey: c.placement_suggestion.join_key ?? undefined,
        }
      : undefined,
    deskState: patchbayExpectationFromCase(c),
    queueLane: c.lane ?? undefined,
    ledgerOpen: c.open_obligations_count ?? (c.ledger_state === 'open' ? 1 : 0),
    nextMove: c.next_move
      ? {
          text: c.next_move.text,
          owner: c.next_move.owner_label ?? c.next_move.owner_name ?? c.next_move.owner?.name ?? 'Us',
          due: fmt(c.next_move.due_at),
          state: c.next_move.state,
          proposedBy: c.next_move.proposed_by,
          overdue: c.next_move.overdue,
          ownerKind: c.next_move.owner_kind,
          dueAt: c.next_move.due_at ?? undefined,
          ownerUserId: c.next_move.owner?.id,
          ownerContractorId: c.next_move.owner_contractor_id ?? undefined,
          ownerDeskAgentId: c.next_move.owner_desk_agent_id ?? undefined,
          moveId: c.next_move.id,
          followedUp: c.next_move.followed_up_at ? fmt(c.next_move.followed_up_at) : undefined,
          ownerReplied: c.next_move.owner_replied_at ? fmt(c.next_move.owner_replied_at) : undefined,
        }
      : undefined,
    callback: c.callback
      ? {
          obligationId: c.callback.obligation_id ?? undefined,
          callerName: c.callback.caller_name ?? undefined,
          callerNumber: c.callback.caller_number ?? undefined,
          duration: c.callback.duration ?? undefined,
          callHref: c.callback.call_href ?? undefined,
          receivedAt: fmt(c.callback.received_at),
        }
      : undefined,
    rileyCall: c.riley_call
      ? {
          turnId: c.riley_call.turn_id,
          state: c.riley_call.state,
          outcome: c.riley_call.outcome ?? undefined,
          callerName: c.riley_call.caller_name ?? undefined,
          callerNumber: c.riley_call.caller_number ?? undefined,
          language: c.riley_call.language,
          liveSummary: c.riley_call.live_summary ?? undefined,
          startedAt: fmt(c.riley_call.started_at),
          endedAt: c.riley_call.ended_at ? fmt(c.riley_call.ended_at) : undefined,
          recordingUrl: c.riley_call.recording_url ?? undefined,
        }
      : undefined,
    ...symptomFields(c),
    caseSummary: structuredSummary(c.case_summary),
    triageNeedsHuman: c.triage_needs_human ?? undefined,
    triageSafety: c.triage_safety ?? undefined,
    triageCategory: c.triage_category ?? undefined,
    coverage: coverageView(c.coverage ?? []),
    summary: c.summary_md ?? c.summary_line ?? '',
    threads: shown,
    // Riley rail parts (slice 17) read their own endpoints; the view keeps the fields typed.
    trace: [],
    chat: [],
    ask: { trace: [], finding: '', actions: [] },
    asOf: asOf ? fmt(asOf) : undefined,
    source: opts.source,
    conduitCustomerId: c.conduit_customer_id ?? undefined,
    deskAccountId: c.desk_account_id ?? undefined,
    accountCustomerSuggestion: c.account_customer_suggestion?.conduit_customer_id
      ? {
          conduitCustomerId: c.account_customer_suggestion.conduit_customer_id,
          customerDisplay: c.account_customer_suggestion.customer_display ?? undefined,
        }
      : undefined,
    customerEmailWarning: c.customer_email_warning
      ? {
          deskTicketId: c.customer_email_warning.desk_ticket_id,
          ticketNumber: c.customer_email_warning.ticket_number,
          message: c.customer_email_warning.message,
          noCustomerEmailSent: c.customer_email_warning.no_customer_email_sent,
        }
      : undefined,
    contactId: c.contact_id ?? undefined,
    crmAccountId: c.crm_account_id ?? undefined,
    conduitSystemId: c.conduit_phone_system_id ?? undefined,
    tags: c.tags ?? [],
    unclassified,
    mergedInto: c.merged_into ?? undefined,
    allTicketsDeletedInDesk: c.all_tickets_deleted_in_desk ?? false,
    removedAt: c.removed_at ?? undefined,
    removalReason: c.removal_reason ?? undefined,
    duplicateOfCaseId: c.duplicate_of_case_id ?? undefined,
  }
}

const DOMAIN: Record<string, CoverageView['domain']> = { phone: 'Phone', it: 'IT', macd: 'MACD' }

function symptomFields(c: ApiCase): Pick<CaseView, 'symptom' | 'symptomSource' | 'symptomFull' | 'symptomFirst' | 'symptomNewest'> {
  const h = headline(c)
  // More shows the turns without their signatures and legal footers too.
  const first = c.symptom_first ? stripFooter(plainMarkup(c.symptom_first)) : ''
  const newest = c.symptom_latest ? stripFooter(plainMarkup(c.symptom_latest)) : ''
  const same = first.replace(/\s+/g, ' ') === newest.replace(/\s+/g, ' ')
  return {
    symptom: h.text,
    symptomSource: h.source,
    // The dispatch drawer and Draft from case keep the fuller wording.
    symptomFull: symptomLine(c.symptom_first, c.symptom_latest),
    symptomFirst: first || undefined,
    symptomNewest: newest && !same ? newest : undefined,
  }
}

function coverageView(coverage: NonNullable<ApiCase['coverage']>): CoverageView[] {
  return coverage.map((l) => ({
    domain: DOMAIN[l.domain] ?? 'Phone',
    // Unknown carries its reason as the value ("nothing matched; the contract line decides", "Customer not
    // linked in Conduit", "Conduit unreachable"); a decision shows the contract it came from underneath.
    state: l.decision === 'unknown' ? l.basis : l.decision,
    basis: l.decision === 'unknown' ? '' : l.basis,
  }))
}

function toContextView(ctx: ApiContext, coverage: NonNullable<ApiCase['coverage']>): ContextView {
  const c = (ctx.context ?? {}) as {
    customer?: { service_city?: string }
    cmdb?: { remote_path?: ContextView['remotePath']; remote_basis?: string; hardware_identity?: string }
    dossier?: { handling?: string | null }
    live?: Record<string, unknown>
  }
  const live = Object.entries(c.live ?? {})
    .filter((entry): entry is [string, string] => entry[0].endsWith('_note') && typeof entry[1] === 'string' && entry[1].trim() !== '')
    .map(([k, v]) => {
      const key = k.replace(/_note$/, '')
      const zabbix = key === 'wan'
      return {
        source: zabbix ? 'Zabbix WAN' : key.toUpperCase(),
        state: v.trim(),
        at: fmt(ctx.built_at),
        ...(zabbix ? { provenance: 'Riley /context live.wan_note (Zabbix, read-only)' } : {}),
      }
    })
  return {
    coverage: coverageView(coverage),
    remotePath: c.cmdb?.remote_path ?? 'unknown',
    remoteBasis: c.cmdb?.remote_basis,
    site: c.customer?.service_city,
    systems: c.cmdb?.hardware_identity ? [c.cmdb.hardware_identity] : [],
    handling: c.dossier?.handling ?? undefined,
    // Not in Riley's /context; the count comes from Conduit once api-cases exposes it.
    credentials: null,
    lastKnown: live,
    builtAt: fmt(ctx.built_at),
    stale: ctx.stale,
    noContext: ctx.state === 'no_context' || (!ctx.ok && !ctx.context),
  }
}

function toWorkItem(w: ApiWorkItem): WorkItemView {
  const done = w.progress?.done
  const total = w.progress?.total
  return {
    id: w.conduit_work_item_id,
    kind: w.kind,
    title: w.title,
    status: w.status,
    due: w.due_on ?? undefined,
    checklist: typeof done === 'number' && typeof total === 'number' ? { done, total } : undefined,
  }
}

function toWorkOrder(w: ApiWorkOrder): WorkOrderView {
  // Two WOs for the same firm on one case (#100) each show their own PO state and their own invoice.
  const po = w.books_po_number ?? w.po_number
  return {
    id: w.id ?? undefined,
    contractorId: w.assignee_kind !== 'internal_tech' ? (w.contractor_id ?? undefined) : undefined,
    number: w.case_scoped_label ?? w.display_number ?? 'WO',
    status: w.status === 'approved' && w.payment_status === 'paid' ? 'paid' : w.status,
    contractor: w.contractor_display ?? (w.assignee_kind === 'internal_tech' ? 'our technician' : 'contractor'),
    window: w.scheduled_start ? formatOfficeWindow(w.scheduled_start, w.time_precision) : '',
    hours: w.minutes_total === null ? 'hours: from invoice' : undefined,
    nte: w.po_label ?? (po ? poText(po) : ''),
    tasks: `${w.tasks_done ?? 0}/${w.tasks_total ?? 0} tasks`,
    invoices: w.invoices?.length ? w.invoices.join(', ') : 'no invoice yet',
  }
}

export const __test = { toCaseView, toContextView, inlineAnswers, symptomLine, firstCustomerLine, headline, stripFooter }
export type { ApiCase, ApiThreadTurns, RelatedView }
